import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarCheck, LuChartLine, LuFolderKanban, LuLoaderCircle, LuWalletCards } from 'react-icons/lu';
import DetailPane from '../../../components/data/DetailPane';
import useEmployeeAnalysisText from '../employeeAnalysisText';
import { GRADIENT, workerInitials } from './WorkerCell';

const SECTION_TITLE = 'tw-px-2 tw-pb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';

/**
 * The selected worker (the old expanded row and action bar together): FTE
 * Yönetimi and İstatistikler, every project assignment with its visits and
 * expense (and "Visitler"), and the tariff list with its total.
 */
const WorkerDetailPane = ({
  docked, height, worker, tariffWorker, totalRate, isAdmin, visitCounts, expenses, detailsLoading, checkingKey,
  position, onPrev, onNext, onClose, onFte, onStatistics, onVisits,
}) => {
  const { label, formatMoney, formatDate } = useEmployeeAnalysisText();
  const paneLabel = label('panel.label');

  if (!worker) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const name = `${worker.name || ''} ${worker.surname || ''}`.trim();
  const projectsWithRates = (tariffWorker?.projects || []).filter((project) => project.rate);

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={worker.id}
      identity={{
        avatar: workerInitials(worker),
        gradient: GRADIENT,
        title: name || '-',
        subtitle: detailsLoading
          ? label('panel.projectsOnly', { projects: worker.projects.length })
          : label('panel.summary', { projects: worker.projects.length, visits: worker.totals.visits, expense: formatMoney(worker.totals.expense) }),
      }}
      actions={[
        { key: 'fte', label: label('panel.fte'), icon: LuWalletCards, tint: 'emerald', onClick: onFte },
        { key: 'statistics', label: label('panel.statistics'), icon: LuChartLine, tint: 'violet', onClick: onStatistics },
      ]}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    >
      <section aria-label={label('panel.assignments')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.assignments')} · {worker.projects.length}</p>
        <ul className="tw-space-y-1.5">
          {worker.projects.map((project) => {
            const key = `${worker.id}-${project.projectId}`;
            const checking = checkingKey === key;
            return (
              <li key={key} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <div className="tw-flex tw-items-start tw-gap-2.5">
                  <span className="tw-mt-0.5 tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-sky-500/10 tw-text-sky-600 dark:tw-text-sky-300">
                    <LuFolderKanban aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </span>
                  <div className="tw-min-w-0 tw-flex-1">
                    <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{project.projectTitle}</p>
                    <p className="tw-truncate tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">
                      {[project.role || '-', formatDate(project.assignedAt)].join(' · ')}
                    </p>
                    {isAdmin && project.rate && (
                      <p className="tw-mt-0.5 tw-text-[11px] tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">
                        {label('panel.rate', { type: project.rate.type || '-', value: project.rate.value ?? '-' })}
                      </p>
                    )}
                  </div>
                </div>
                <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-slate-200/70 tw-pt-2 dark:tw-border-white/[0.06]">
                  <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    {detailsLoading ? label('panel.loadingDetails') : label('panel.assignmentTotals', { visits: visitCounts[key] || 0, expense: formatMoney(expenses[key] || 0) })}
                  </span>
                  <button
                    type="button"
                    onClick={() => onVisits(project)}
                    disabled={checking}
                    className="tw-inline-flex tw-h-7 tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-blue-500/25 tw-bg-blue-500/10 tw-px-2.5 tw-text-xs tw-font-semibold tw-text-blue-700 hover:tw-bg-blue-500/15 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-blue-500/40 disabled:tw-opacity-60 dark:tw-text-blue-300"
                  >
                    {checking ? <LuLoaderCircle aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-animate-spin" /> : <LuCalendarCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
                    {label('panel.visits')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-label={label('panel.tariffs')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.tariffs')}</p>
        {projectsWithRates.length === 0 ? (
          <p className="tw-px-2 tw-text-sm tw-italic tw-text-slate-400">{label('panel.noTariffs')}</p>
        ) : (
          <div className="tw-rounded-2xl tw-border tw-border-emerald-500/20 tw-bg-emerald-500/[0.05] tw-p-2">
            <ul className="tw-space-y-0.5">
              {projectsWithRates.map((project, index) => (
                <li key={`${project.projectId}-${index}`} className="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-px-2 tw-py-1">
                  <span className="tw-min-w-0 tw-truncate tw-text-sm tw-text-slate-700 dark:tw-text-slate-200">
                    <span className="tw-font-semibold">{project.projectTitle}</span>
                    {project.role && <span className="tw-text-slate-500 dark:tw-text-slate-400"> ({project.role})</span>}
                  </span>
                  <span className="tw-shrink-0 tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{project.rate.type}: {project.rate.value}</span>
                </li>
              ))}
            </ul>
            <p className="tw-mt-1 tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-border-t tw-border-emerald-500/20 tw-px-2 tw-pt-1.5 tw-text-sm">
              <span className="tw-font-semibold tw-text-emerald-800 dark:tw-text-emerald-200">{label('panel.tariffTotal')}</span>
              <span className="tw-font-bold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{totalRate.toLocaleString()}</span>
            </p>
          </div>
        )}
      </section>
    </DetailPane>
  );
};

WorkerDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  worker: PropTypes.object,
  tariffWorker: PropTypes.object,
  totalRate: PropTypes.number,
  isAdmin: PropTypes.bool,
  visitCounts: PropTypes.object.isRequired,
  expenses: PropTypes.object.isRequired,
  detailsLoading: PropTypes.bool,
  checkingKey: PropTypes.string,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onFte: PropTypes.func.isRequired,
  onStatistics: PropTypes.func.isRequired,
  onVisits: PropTypes.func.isRequired,
};

export default WorkerDetailPane;
